/**
 * 모듈 트리 목록 — 표시 명칭 사전순(숫자 인식) 정렬·삽입.
 * Array / String / Module 공통. 명칭이 같으면 entity seq로 순서를 고정한다.
 */

const TREE_NAME_COMPARE_OPTS = { numeric: true, sensitivity: 'base' };

function parseEntitySeq(value) {
  if (value == null || value === '') {
    return null;
  }
  const raw = String(value);
  const stripped = raw.replace(/^(array|string|module)_/i, '');
  const num = Number(stripped);
  return Number.isFinite(num) ? num : stripped;
}

export function readModuleTreeNodeSeq(node) {
  if (!node) {
    return null;
  }
  return parseEntitySeq(
    node.solarModuleSeq ?? node.solarStringSeq ?? node.solarArraySeq ?? node.seq ?? node.id
  );
}

export function readFlatModuleSeq(record) {
  if (!record) {
    return null;
  }
  return parseEntitySeq(record.id ?? record.seq);
}

function readTypePrefix(item) {
  const kind = item?.type || item?.level;
  if (kind === 'array') {
    return 'Array';
  }
  if (kind === 'string') {
    return 'String';
  }
  if (kind === 'module') {
    return 'Module';
  }
  return '';
}

/** 트리에 보이는 명칭 (name 없으면 Array/String/Module {displayNo}) */
export function readPanelTreeListName(item) {
  if (!item) {
    return '';
  }
  if (item.name != null && String(item.name).trim() !== '') {
    return String(item.name);
  }
  const displayNo =
    item.displayNo ??
    item.moduleId ??
    item.solarModuleId ??
    item.stringId ??
    item.solarStringId ??
    item.arrayId;
  const prefix = readTypePrefix(item);
  if (displayNo != null && displayNo !== '') {
    return prefix ? `${prefix} ${displayNo}` : String(displayNo);
  }
  return '';
}

/** 트리 행에 보이는 값과 동일 (Array/String/Module 접두 제거) */
export function readPanelTreeSortKey(item) {
  const rawName = readPanelTreeListName(item).trim();
  const stripped = rawName.replace(/^(Module|String|Array)\s+/i, '').trim();
  if (stripped) {
    return stripped;
  }
  const displayNo =
    item?.displayNo ??
    item?.arrayId ??
    item?.stringId ??
    item?.moduleId ??
    item?.solarModuleId ??
    item?.solarStringId;
  return displayNo != null && displayNo !== '' ? String(displayNo) : '';
}

function parseSortNumber(key) {
  if (key == null || key === '') {
    return null;
  }
  const trimmed = String(key).trim();
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed);
  }
  const tail = trimmed.match(/(\d+)\s*$/);
  return tail ? Number(tail[1]) : null;
}

/** @deprecated readPanelTreeListName 사용 */
export function readModuleListName(item) {
  return readPanelTreeListName(item);
}

export function comparePanelTreeListItems(a, b) {
  const keyA = readPanelTreeSortKey(a);
  const keyB = readPanelTreeSortKey(b);
  const numA = parseSortNumber(keyA);
  const numB = parseSortNumber(keyB);
  if (numA != null && numB != null && numA !== numB) {
    return numA - numB;
  }
  const nameCmp = keyA.localeCompare(keyB, undefined, TREE_NAME_COMPARE_OPTS);
  if (nameCmp !== 0) {
    return nameCmp;
  }
  return compareModuleSeqValues(
    readModuleTreeNodeSeq(a) ?? readFlatModuleSeq(a),
    readModuleTreeNodeSeq(b) ?? readFlatModuleSeq(b)
  );
}

export function compareModuleListItems(a, b) {
  return comparePanelTreeListItems(a, b);
}

export function compareModuleSeqValues(a, b) {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function compareModuleTreeNodesBySeq(a, b) {
  return compareModuleSeqValues(readModuleTreeNodeSeq(a), readModuleTreeNodeSeq(b));
}

export function findPanelTreeInsertIndex(nodes, node) {
  const list = nodes || [];
  for (let i = 0; i < list.length; i++) {
    if (comparePanelTreeListItems(node, list[i]) < 0) {
      return i;
    }
  }
  return list.length;
}

export function findModuleTreeInsertIndex(nodes, moduleNode) {
  return findPanelTreeInsertIndex(nodes, moduleNode);
}

export function findFlatModuleInsertIndex(modules, moduleRecord) {
  const list = modules || [];
  for (let i = 0; i < list.length; i++) {
    if (compareModuleListItems(moduleRecord, list[i]) < 0) {
      return i;
    }
  }
  return list.length;
}

export function insertModuleTreeNodeInSeqOrder(nodes, moduleNode) {
  if (!moduleNode) {
    return nodes || [];
  }
  const list = nodes || [];
  const idx = findModuleTreeInsertIndex(list, moduleNode);
  list.splice(idx, 0, moduleNode);
  return list;
}

export function insertFlatModuleInSeqOrder(modules, moduleRecord) {
  if (!moduleRecord) {
    return modules || [];
  }
  const list = modules || [];
  const idx = findFlatModuleInsertIndex(list, moduleRecord);
  list.splice(idx, 0, moduleRecord);
  return list;
}

/** 트리 형제 목록을 화면 표시 번호 순으로 in-place 정렬 */
export function sortPanelTreeListsInPlace(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) {
    return nodes;
  }
  if (nodes.length >= 2) {
    nodes.sort(comparePanelTreeListItems);
  }
  nodes.forEach(node => {
    if (node?.children?.length) {
      sortPanelTreeListsInPlace(node.children);
    }
  });
  return nodes;
}
